import {
  IRapportMallControl,
  IRapportMallRect,
  isRapportConditionControl,
  isRapportContainerControl,
} from './RapportMallTypes';

/** Skala för att översätta millimeter i mallen till pixlar på skärmen */
export const MM_TO_PIXEL_SCALE = 4;

export const SELECTED_COLOR = '#1976d2';

export const SELECTED_SHADOW = `inset 0 0 0 1px ${SELECTED_COLOR}, 0 0 0 1px ${SELECTED_COLOR}`;
export const HOVER_SHADOW = `inset 0 0 0 1px ${SELECTED_COLOR}, 0 0 0 1px ${SELECTED_COLOR}`;

export const AVENY_REPORT_TITLE_HEIGHT = 30;
export const AVENY_REPORT_TITLE_HEIGHT_SMALL = 15;
export const AVENY_REPORT_TITLE_FONT_SIZE = 13;

export const AVENY_REPORT_DEFAULT_BAND_HEIGHT = 5; // mm
export const AVENY_REPORT_EDITBOX_HEIGHT = 5; // mm
export const AVENY_REPORT_CHECKBOX_HEIGHT = 5; // mm
export const AVENY_REPORT_DEFAULT_SPACING = 5; // mm

export const AVENY_REPORT_DEFAULT_LINE_MARGIN = 1; // mm

/**
 * Genererar ett CSS-stilobjekt baserat på kontrollens position och dimensioner.
 *
 * @param {IRapportMallRect} ctrl - Kontrollobjektet som innehåller egenskaper för position och dimensioner.
 * @returns {React.CSSProperties} Ett CSS-stilobjekt med beräknade position och dimensioner.
 */
export function getPositionStyle(
  ctrl: IRapportMallRect,
  inContainer: boolean,
  maxWidth: number,
  defaultHeight?: number
): React.CSSProperties {
  const style: React.CSSProperties = {};

  const width =
    typeof ctrl.Width === 'number'
      ? Math.min(ctrl.Width, maxWidth - (ctrl.Left ?? 0))
      : maxWidth - (ctrl.Left ?? 0);
  if (typeof ctrl.Right === 'number') {
    if (typeof ctrl.Left === 'number') {
      style.left = ctrl.Left * MM_TO_PIXEL_SCALE;
      style.width = (ctrl.Right - ctrl.Left) * MM_TO_PIXEL_SCALE;
    } else {
      style.left = (ctrl.Right - width) * MM_TO_PIXEL_SCALE;
      style.width = width * MM_TO_PIXEL_SCALE;
    }
  } else {
    style.left = (ctrl.Left ?? 0) * MM_TO_PIXEL_SCALE;
    style.width = width * MM_TO_PIXEL_SCALE;
  }

  const height = typeof ctrl.Height === 'number' ? ctrl.Height : defaultHeight;
  if (typeof ctrl.Bottom === 'number') {
    style.position = 'absolute';
    if (typeof ctrl.Top === 'number') {
      style.top = ctrl.Top * MM_TO_PIXEL_SCALE;
      style.height = (ctrl.Bottom - ctrl.Top) * MM_TO_PIXEL_SCALE;
    } else {
      style.top = (ctrl.Bottom - (height ?? 0)) * MM_TO_PIXEL_SCALE;
      style.height = (height ?? 0) * MM_TO_PIXEL_SCALE;
    }
  } else if (typeof ctrl.Top === 'number' || inContainer) {
    style.position = 'absolute';
    style.top =
      (typeof ctrl.Top === 'number' ? ctrl.Top : 0) * MM_TO_PIXEL_SCALE;
    style.height = (height ?? 0) * MM_TO_PIXEL_SCALE;
  } else {
    style.position = 'relative';
    if (height !== undefined) {
      style.height = height * MM_TO_PIXEL_SCALE;
    }
  }

  return style;
}

/**
 * Hittar en kontroll i rapportstrukturen via dess sökväg.
 * @param parsedMall Det tolkade rapportmall-objektet.
 * @param path Sökvägen till kontrollen.
 * @returns Den funna kontrollen eller null.
 */
export function getControlByPath(
  parsedMall: any,
  path: number[] | null
): IRapportMallControl | null {
  if (!parsedMall || !path || !parsedMall.Body) return null;
  let current: any = parsedMall.Body;
  for (let i = 0; i < path.length; i++) {
    current = current[path[i]];
    if (!current) return null;
    if (
      i < path.length - 1 &&
      (isRapportContainerControl(current) || isRapportConditionControl(current))
    ) {
      current = current.Content;
    }
  }
  return current || null;
}

// Summera antalet kontroller i en kontroll, rekursivt
// Container och Condition kontroller kan ha flera kontroller i sig
export const controlCount = (ctrl: IRapportMallControl): number => {
  if (isRapportConditionControl(ctrl)) {
    // Condition: summera rekursivt alla i Content
    if (ctrl.Content && ctrl.Content.length > 0) {
      return ctrl.Content.reduce(
        (sum: number, child: IRapportMallControl) => sum + controlCount(child),
        0
      );
    }
    return 0;
  } else if (isRapportContainerControl(ctrl)) {
    // Container: summera rekursivt alla i Content + 1 för containern själv
    if (ctrl.Content && ctrl.Content.length > 0) {
      return (
        1 +
        ctrl.Content.reduce(
          (sum: number, child: IRapportMallControl) =>
            sum + controlCount(child),
          0
        )
      );
    }
    return 1;
  }
  return 1; // Om det inte är en Condition/Container, räkna kontrollen själv
};

// Alternativlistor för Autocomplete
export const ALIGN_OPTIONS = [
  { label: 'Vänster', value: 'Left' },
  { label: 'Centrerad', value: 'Center' },
  { label: 'Höger', value: 'Right' },
] as const;
export const VALIGN_OPTIONS = [
  { label: 'Topp', value: 'Top' },
  { label: 'Centrerad', value: 'Center' },
  { label: 'Botten', value: 'Bottom' },
] as const;
export const TEXTALIGN_OPTIONS = [
  { label: 'Vänster', value: 'Left' },
  { label: 'Centrerad', value: 'Center' },
  { label: 'Höger', value: 'Right' },
] as const;

/**
 * Beräknar en lista av zIndex-värden för en lista av kontroller,
 * så att varje toppkontroll och dess underkontroller får plats i zIndex-flödet.
 * @param controls Kontroller att beräkna zIndex för
 * @param startZIndex Startvärde för zIndex (default 1)
 * @returns Array med zIndex för varje toppkontroll
 */
export function getZIndexesForControls(
  controls: IRapportMallControl[],
  startZIndex: number = 1
): number[] {
  const zIndexes: number[] = [];
  let currentZ = startZIndex;
  controls.forEach(child => {
    zIndexes.push(currentZ);
    currentZ += controlCount(child);
  });
  return zIndexes;
}
